import DefaultLayout from '../layouts/defaultLayout';
import { Box, Button, Skeleton, TextField, Typography } from '@mui/material';
import ContentsTable from '../components/ContentsTable';
import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query as firebaseQuery,
  startAfter,
  where,
  getDoc
} from 'firebase/firestore';
import { showToast } from '../utils/toast';
import { db } from '../firebase/firebaseConfig';
import { IS_DEMO } from '../utils/config';

function documentDataToObject(doc) {
  return { id: doc.id, ...doc.data() };
}

async function fetchPosts(query) {
  
  try {
    const snapshot = await getDocs(query);
    const _fetched = snapshot.docs.map(documentDataToObject);
    const settingsRef = doc(db, 'settings', 'config');
    const { banner_id } = (await getDoc(settingsRef)).data();
    return {
      data: _fetched,
      banner_id,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      isEnded: snapshot.docs.length !== postLimit
    };
  } catch (error) {
    
    showToast({ type: 'error', message: error.message });
    return { data: [] };
  }
}

const postLimit = 10;

export default function Contents() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnded, setIsEnded] = useState(false);
  const [lastDoc, setLastDoc] = useState();
  const [searchVal, setSearchVal] = useState(null);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(null);
  const [bannerId, setBannerId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setIsEnded(false);
    setLastDoc(null);
    setPosts([]);
    _fetch(false);
  }, []);

  useEffect(() => {
    if (searchVal === null) return;
    _fetch(false);
  }, [debouncedSearchValue]);

  // Debounce
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchValue(searchVal);
    }, 500);

    return () => clearTimeout(timerId);
  }, [searchVal]);

  function _fetch(startAfterLastDoc = true) {
    if (!startAfterLastDoc) {
      setLoading(true);
      setIsEnded(false);
      setLastDoc(null);
      setPosts([]);
    }
    const queries = [collection(db, 'posts'), orderBy('dateCreated', 'desc'), limit(postLimit)];

    const whereQuery = [];

    if (startAfterLastDoc && lastDoc) {
      whereQuery.push(startAfter(lastDoc));
    }
    if (searchVal) {
      whereQuery.push(where('search', 'array-contains', `${searchVal}`.toLowerCase()));

      fetchPosts(firebaseQuery(...[...queries, ...whereQuery])).then((r) => {
        setPosts((prev) => [...prev, ...r.data]);
        setBannerId(r.banner_id);
        if (r.isEnded !== undefined) setIsEnded(r.isEnded);
        if (r.lastDoc) setLastDoc(r.lastDoc);
        setLoading(false);
      });
    } else {
      fetchPosts(firebaseQuery(...[...queries, ...whereQuery])).then((r) => {
        
        setPosts((prev) => [...prev, ...r.data]);
        setBannerId(r.banner_id);
        if (r.isEnded !== undefined) setIsEnded(r.isEnded);
        if (r.lastDoc) setLastDoc(r.lastDoc);
        setLoading(false);
      });
    }
  }

  function onClickLoadMore() {
    _fetch(true);
  }

  function onDelete(id) {
    if (IS_DEMO) {
      return showToast({
        type: 'error',
        message: 'This is a demo version. You cannot delete content.'
      });
    }

    const confirm = window.confirm('Are you sure you want to delete this post?');
    if (!confirm) return;

    setPosts((old) => old.filter((val) => val.id !== id));
    const sfDocRef = doc(db, 'posts', id);
    deleteDoc(sfDocRef);
  }

  return (
    <DefaultLayout>
      <Box gap={3} display="flex" flexDirection="column">
        <Typography color="primary.main" fontWeight={400} variant="h4">
          All Files
        </Typography>
        <Box gap={2} width="100%" justifyContent="flex-end" alignItems="center" display="flex">
          <TextField
            onChange={(e) => setSearchVal(e.target.value)}
            size="medium"
            label="(e.g. title, category, paid)"
          />
        </Box>
        {posts.length === 0 ? (
          <>
            <Skeleton sx={{ borderRadius: 2 }} variant="rectangular" width="100%" height="100px" />
            <Skeleton sx={{ borderRadius: 2 }} variant="rectangular" width="100%" height="100px" />
            <Skeleton sx={{ borderRadius: 2 }} variant="rectangular" width="100%" height="100px" />
          </>
        ) : (
          <ContentsTable posts={posts} bannerId={bannerId} onDelete={onDelete} />
        )}
        <div
          style={{
            margin: 'auto',
            padding: '10px',
            width: '100%',
            maxWidth: '250px',
            paddingTop: '30px'
          }}>
          {isEnded ? (
            <Button disabled fullWidth variant="outlined">
              No more posts
            </Button>
          ) : (
            <Button onClick={onClickLoadMore} fullWidth variant="outlined">
              Load More
            </Button>
          )}
        </div>
      </Box>
    </DefaultLayout>
  );
}
